import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";

// HP4 — a conventional customer-reviews layout: an "Our Community" header
// plus a simple responsive grid of review cards (star rating · quote · name
// · product). Uses only Riversol's verified {name + quote + product + 5★}.
const REVIEWS = [
  { name: "Brenda U.", product: "Anti-Aging Trio Bundle", quote: "The trio is a great skincare routine. This is the first product I've tried in many years that hasn't triggered my eczema. I've been using the Riversol trio for about a year and I love the way my skin feels." },
  { name: "Marlene B.", product: "Daily Glow Mineral Sunscreen SPF 50+", quote: "Love the consistency, shade and coverage of the Daily Glow Mineral Sunscreen — it's my new favourite facial sunscreen! And at 50+ SPF I know my delicate facial skin is well covered." },
  { name: "JLS", product: "Daily Moisturizing Cream", quote: "As a previous patient of Dr. Rivers I was confident his products would be safe and effective. I have been using them since the inception of the line." },
  { name: "Maryanne", product: "Peptide Hand Cream", quote: "Not greasy and fast absorbing. My hands are so soft, the age spots are less noticeable and there's a definite reduction in wrinkles. I was sceptical after so many hand creams with no results — but Riversol came to the rescue." },
  { name: "Marlene R.", product: "Anti-Aging Serum", quote: "Love this serum. I use it in the morning after my shower. My skin feels hydrated and smooth." },
  { name: "Denise C.", product: "Hydrating Cream Cleanser", quote: "I love the look and feel of my face after I use this cleanser. Gentle, never tight or dry." },
];

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: n }).map((_, k) => (
      <Star key={k} size={15} strokeWidth={0} className="fill-riversol-blue text-riversol-blue" />
    ))}
  </div>
);

export const ReviewsGridV4 = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section ref={ref} className="bg-riversol-sand py-20 lg:py-28" data-testid="hp4-reviews">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 lg:mb-14">
          <div>
            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-4">Our community</div>
            <h2 className="text-[32px] sm:text-[38px] lg:text-[48px] leading-[1.05] tracking-[-0.02em] text-neutral-900">
              115,000+ <span className="italic text-riversol-blue">happy customers.</span>
            </h2>
            <div className="mt-4 flex items-center gap-3">
              <Stars n={5} />
              <span className="text-[13px] text-neutral-600">4.8 average across 30,000+ verified reviews</span>
            </div>
          </div>
          <a
            href="https://www.riversol.com/pages/reviews"
            data-testid="hp4-reviews-all"
            className="group shrink-0 inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase font-medium text-riversol-blue border-b border-riversol-blue/40 pb-1.5 hover:border-riversol-blue transition-colors"
          >
            View all reviews
            <ArrowRight size={14} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={r.name + i}
              data-testid={`hp4-review-${i}`}
              initial={{ opacity: 0, y: 26 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 + Math.floor(i / 3) * 0.05 }}
              className="flex flex-col bg-white border border-neutral-200 rounded-sm p-7 lg:p-8"
            >
              <Stars n={5} />
              <blockquote className="mt-4 text-[15px] leading-relaxed text-neutral-700 flex-1">
                “{r.quote}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-neutral-100 flex items-center gap-3">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-900">{r.name}</span>
                <span className="w-1 h-1 rounded-full bg-neutral-300" />
                <span className="text-[12px] text-neutral-500">{r.product}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsGridV4;
